import { v1 as uuid } from 'uuid'

import { cannot } from '@/app/helpers'
import { hasContent } from '@/app/module/campaigns/helpers'
import {
  CampaignTemplateType,
  ContentAudio,
  ContentSMS,
  MultimessageItem,
  PartFamily,
} from '@/app/module/campaigns/types'

type WelcomeContent =
  | { type: PartFamily.Voice; audio: ContentAudio | null }
  | { type: PartFamily.SMS; message: ContentSMS | null }

export type WelcomeMessageData = WelcomeContent & { id: string; hasQuestion: false }
export type WelcomeChange = Partial<WelcomeMessageData>

export const getWelcomeContent = (item: MultimessageItem): WelcomeContent | undefined => {
  switch (item.type) {
    case CampaignTemplateType.VoiceSurvey:
      return { type: PartFamily.Voice, audio: item.variables.welcome ?? null }
    case CampaignTemplateType.SMSSurvey:
      return { type: PartFamily.SMS, message: item.variables.welcome ?? null }
    case CampaignTemplateType.CATI:
    case CampaignTemplateType.Drip:
    case CampaignTemplateType.Reminder:
    case CampaignTemplateType.WhatsAppSurvey:
      return undefined
    default:
      return cannot(item)
  }
}

export const createWelcomeMessage = (item: MultimessageItem): WelcomeMessageData | undefined => {
  const content = getWelcomeContent(item)
  return content ? { ...content, hasQuestion: false, id: uuid() } : undefined
}

export const hasWelcomeContent = (content: WelcomeContent | undefined): boolean => {
  if (!content) return false

  switch (content.type) {
    case PartFamily.Voice:
      return (
        hasContent(content.audio, 'playfile') || hasContent(content.audio, 'say') || hasContent(content.audio, 'voice')
      )
    case PartFamily.SMS:
      return hasContent(content.message, 'text')
    default:
      return cannot(content)
  }
}

export const updateWelcomeContent = (item: MultimessageItem, value: WelcomeChange): MultimessageItem => {
  switch (item.type) {
    case CampaignTemplateType.VoiceSurvey: {
      const audio = 'audio' in value ? value.audio : undefined
      return {
        ...item,
        variables: {
          ...item.variables,
          welcome: audio && hasWelcomeContent({ type: PartFamily.Voice, audio }) ? { ...audio } : null,
        },
      }
    }
    case CampaignTemplateType.SMSSurvey: {
      const message = 'message' in value ? value.message : undefined
      const welcome =
        message && hasWelcomeContent({ type: PartFamily.SMS, message }) ? { ...message, language: 'en' } : null
      return { ...item, variables: { ...item.variables, welcome } }
    }
    case CampaignTemplateType.CATI:
    case CampaignTemplateType.Drip:
    case CampaignTemplateType.Reminder:
    case CampaignTemplateType.WhatsAppSurvey:
      return item
    default:
      return cannot(item)
  }
}
